import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, SpellCheck, BookOpenCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const STATUS = { new: ["Open", "#FBBF24"], saved: ["Saved", "#C4B5FD"], fixed: ["Fixed", "#34D399"], dismissed: ["Dismissed", "#A8A090"], sent: ["Sent to Notes", "#60A5FA"], expired: ["Not acted on", "#A8A090"] };

const Row = ({ s, onFixed }) => (
  <div className="rounded border border-[#2D3442] bg-[#13171F] p-2.5 text-xs" data-testid={`history-suggestion-${s.id}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-[#A8A090]">{s.kind === "conflict" ? `Conflict · ${s.entity}` : "Note idea"}</span>
      <span style={{ color: STATUS[s.status]?.[1] }} data-testid={`history-status-${s.id}`}>{STATUS[s.status]?.[0] || s.status}</span>
    </div>
    <div className="mt-1 text-sm text-[#F3EBDD]">{s.kind === "conflict" ? s.issue : s.subject}</div>
    {s.kind === "conflict" && s.status !== "fixed" && <Button size="sm" variant="ghost" className="mt-1 h-7 px-2" onClick={() => onFixed(s)} data-testid={`history-mark-fixed-${s.id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Mark fixed</Button>}
  </div>
);

export const ReviewHistory = ({ cid, onFixed }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["review-history", cid], queryFn: () => api.get(`/items/${cid}/review-history`).then((r) => r.data) });
  if (!data) return null;
  const fixed = async (s) => { await api.patch(`/suggestions/${s.id}`, { status: "fixed" }); qc.invalidateQueries({ queryKey: ["review-history", cid] }); onFixed?.(s.id); };
  const open = data.suggestions.filter((s) => s.kind === "conflict" && s.status !== "fixed" && s.status !== "dismissed").length;
  return (
    <div className="space-y-4" data-testid="review-history">
      <p className="text-xs text-[#A8A090]" data-testid="history-summary">{data.runs.length} review{data.runs.length === 1 ? "" : "s"} · {data.suggestions.filter((s) => s.status === "fixed").length} conflicts fixed · {open} still open</p>
      {data.runs.length === 0 && <p className="text-sm text-[#A8A090]">No reviews yet.</p>}
      {data.runs.map((r) => { const lore = r.kind.startsWith("lore"); return (
        <div key={r.id} data-testid={`history-run-${r.id}`}>
          <div className="mb-2 flex items-center gap-2 text-xs text-[#F3EBDD]">
            {lore ? <BookOpenCheck className="h-3.5 w-3.5 text-[#F59E0B]" /> : <SpellCheck className="h-3.5 w-3.5 text-[#F59E0B]" />}
            <b>{r.kind === "lore_free" ? "Free lore check" : r.kind === "lore" ? "AI lore review" : "Spelling & grammar"}</b>
            <span className="text-[#A8A090]">{new Date(r.at).toLocaleString()} · {lore ? `${r.conflicts} conflicts, ${r.notes} note ideas` : `${r.found} fixes found`} · {r.words} words</span>
          </div>
          {lore && <div className="space-y-2 pl-5">{data.suggestions.filter((s) => s.run_id === r.id).map((s) => <Row key={s.id} s={s} onFixed={fixed} />)}</div>}
        </div>
      ); })}
    </div>
  );
};
